import { Component, OnInit, ViewChild, ElementRef, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable, of, finalize, forkJoin } from 'rxjs';
import { map, startWith, catchError } from 'rxjs/operators';
import { ProjectService } from '../../shared/services/project/project.service';
import { ApiUserService } from '../../shared/services/api-user/api-user.service';
import { RoleService } from '../../shared/services/role/role.service';
import { User, GlobalRole } from '@app/shared/interfaces';
import { AvatarHelper } from '../../shared/helpers/avatar.helper';
import { RoleHelper, UserRole, RoleId } from '../../shared/helpers/role.helper';

import { ProjectInviteRequest, ProjectInviteResponse } from '../../shared/interfaces/project-invite.interface';
import { ToastrService } from 'ngx-toastr';
import { FeatureDialogService } from '../feature-dialog.service';
import { RemoveTeamMemberDialogData } from '../remove-team-member-dialog/remove-team-member-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  hasPublishedVersions as hasPublishedProjectVersions,
  isOperatorRole,
  NO_PUBLISHED_VERSIONS_MESSAGE,
} from '../../shared/helpers/version-visibility.helper';

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  roleId: number;
  isOwner: boolean;
  initials: string;
  isPending?: boolean;
  /** Present only when isPending - used to cancel the open invite. */
  inviteId?: number;
}

export interface InviteTeamMemberDialogData {
  projectId: number;
  currentUserId?: number;
  projectOwnerId?: number;
}

@Component({
  selector: 'app-invite-team-member-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatMenuModule,
    MatDividerModule,
    MatTooltipModule
  ],
  templateUrl: './invite-team-member-dialog.component.html',
  styleUrls: ['./invite-team-member-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InviteTeamMemberDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<InviteTeamMemberDialogComponent>>(MatDialogRef);
  data = inject<InviteTeamMemberDialogData>(MAT_DIALOG_DATA);
  private readonly announcer = inject(LiveAnnouncer);
  private projectService = inject(ProjectService);
  private readonly userService = inject(ApiUserService);
  private readonly roleService = inject(RoleService);
  private toastr = inject(ToastrService);
  private readonly featureDialogService = inject(FeatureDialogService);
  // Selected role ID (default to Contributor - roleId 11)
  selectedRoleId = signal<number>(11);

  // Emails input
  emailCtrl = new FormControl('');

  // Email validation regex
  emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Flag to show email validation error
  showEmailError = signal(false);

  // Available roles
  roles = signal<GlobalRole[]>([]);
  hasPublishedVersions = signal(false);
  readonly noPublishedVersionsMessage = NO_PUBLISHED_VERSIONS_MESSAGE;

  // For email chips
  separatorKeysCodes: number[] = [ENTER, COMMA];
  emails = signal<string[]>([]);

  // Loading states
  isLoading = signal(false);
  isLoadingTeam = signal(false);

  // Progress tracking for multiple invites
  inviteProgress = signal({
    current: 0,
    total: 0,
    isProcessing: false
  });

  // All system users for autocomplete
  allUsers = signal<User[]>([]);
  filteredUsers: Observable<User[]> = of([]);

  // Team members
  teamMembers = signal<TeamMember[]>([]);

  // Current user and project owner tracking
  currentUserId = signal<number | null>(null);
  projectOwnerId = signal<number | null>(null);

  // For focusing the input after selection
  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    // Initialize current user ID and project owner ID from data if provided
    this.currentUserId.set(this.data.currentUserId || null);
    this.projectOwnerId.set(this.data.projectOwnerId || null);

    // Load current user if not provided
    if (!this.currentUserId()) {
      this.loadCurrentUser();
    }

    // Load roles first (doesn't depend on other data)
    this.loadRoles();

    // Load all users for autocomplete and team member email lookup
    if (this.allUsers().length > 0) {
      this.loadTeamMembers();
    } else {
      this.loadUsers();
    }

    // Setup autocomplete filtering
    this.filteredUsers = this.emailCtrl.valueChanges.pipe(
      startWith(null),
      map((email: string | null) => {
        if (!this.allUsers().length) {
          return [];
        }
        if (!email) {
          // When no input, show all users except those already in the project
          return this.allUsers().filter(user => {
            const hasAccess = this.teamMembers().some(member =>
              member.id === user.userId ||
              (member.email && user.email && member.email.toLowerCase() === user.email.toLowerCase())
            );
            return !hasAccess;
          });
        }
        return this._filter(email);
      })
    );
  }

  loadCurrentUser(): void {
    const currentUser$ = this.userService.getCurrentUser?.();
    if (!currentUser$) {
      this.toastr.error('Failed to load current user information');
      return;
    }

    currentUser$.subscribe({
      next: (user) => {
        this.currentUserId.set(user.userId || null);
        console.log('Current user ID:', this.currentUserId());
      },
      error: (error) => {
        console.error('Error loading current user:', error);
        this.toastr.error('Failed to load current user information');
      }
    });
  }

  loadUsers(): void {
    const users$ = this.userService.getAllUsers?.();
    if (!users$) {
      console.error('User service unavailable: cannot load users for autocomplete');
      this.allUsers.set([]);
      this.loadTeamMembers();
      return;
    }

    users$.subscribe({
      next: (users) => {
        this.allUsers.set(users);
        // Re-filter users based on current input
        this.emailCtrl.updateValueAndValidity();

        // Now that we have user data, load team members
        // This ensures we have user details available for the team members
        this.loadTeamMembers();

        // If team members are already loaded, update their emails
        if (this.teamMembers().length > 0) {
          this.updateTeamMemberEmails();
        }
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.toastr.error('Failed to load users for autocomplete');

        // Still try to load team members even if users failed
        this.loadTeamMembers();
      }
    });
  }

  loadTeamMembers(): void {
    this.isLoadingTeam.set(true);

    const project$ = this.projectService.getProject?.(this.data.projectId);
    if (!project$ || typeof (project$ as any).pipe !== 'function') {
      console.error('Project service unavailable: cannot load team members');
      this.isLoadingTeam.set(false);
      this.teamMembers.set([]);
      return;
    }

    // Pending invites are optional context - non-fatal if backend/service unavailable.
    const invites$: Observable<ProjectInviteResponse[]> = (
      this.projectService.getInvitesForProject?.(this.data.projectId) ?? of([])
    ).pipe(
      catchError((err: any) => {
        console.warn('Could not load project invites - pending indicators suppressed:', err);
        return of([] as ProjectInviteResponse[]);
      })
    );

    forkJoin({ project: project$, invites: invites$ }).pipe(
      finalize(() => this.isLoadingTeam.set(false)),
      catchError(error => {
        console.error('Error loading project data:', error);
        let errorMessage = 'Failed to load team members';

        if (error.status === 404) {
          errorMessage = 'Project not found. Please check if the project still exists.';
          console.error('Project not found. Project ID:', this.data.projectId);
        } else if (error.status === 401 || error.status === 403) {
          errorMessage = 'You do not have permission to view this project.';
        } else if (error.status === 500) {
          errorMessage = 'Server error occurred while loading project data.';
        }

        this.toastr.error(errorMessage);
        return of({ project: null, invites: [] as ProjectInviteResponse[] });
      })
    ).subscribe(({ project, invites }) => {
      if (!project) {
        this.teamMembers.set([]);
        return;
      }

      this.hasPublishedVersions.set(hasPublishedProjectVersions(project.versions ?? []));
      this.setDefaultRoleWhenOperatorUnavailable();

      // Set project owner ID if not already set
      if (!this.projectOwnerId() && project.userId) {
        this.projectOwnerId.set(project.userId);
      }

      // 1. Confirmed members from project.users
      const projectUsers = project.users || [];
      const confirmedMembers: TeamMember[] = projectUsers.map(user => {
        const isOwner = user.userId === project.userId;
        const teamMember: TeamMember = {
          id: user.userId || 0,
          name: user.userName || 'Unknown User',
          email: '',
          roleId: user.roleId || 11,
          isOwner,
          initials: user.userName ? this.getInitials(user.userName) : '?',
        };

        if (this.allUsers().length > 0) {
          const matchedUser = this.allUsers().find(u => u.userId === teamMember.id);
          if (matchedUser && matchedUser.email) {
            teamMember.email = matchedUser.email;
          }
        } else {
          teamMember.email = 'Loading...';
        }
        return teamMember;
      });

      // 2. Pending invites (response=Open, not already confirmed) -> render with isPending + clock icon (Figma 3779-186238)
      const confirmedIds = new Set(confirmedMembers.map(m => m.id));
      const pendingMembers: TeamMember[] = (invites ?? [])
        .filter(inv => inv?.response === 'Open' && !!inv.inviteeId)
        .filter(inv => !confirmedIds.has(inv.inviteeId))
        .map(inv => ({
          id: inv.inviteeId,
          name: inv.inviteeName || 'Pending user',
          email: '',
          roleId: inv.roleId || 11,
          isOwner: false,
          initials: inv.inviteeName ? this.getInitials(inv.inviteeName) : '?',
          isPending: true,
          inviteId: inv.inviteId,
        }));

      this.teamMembers.set([...confirmedMembers, ...pendingMembers]);

      this.updateRoleNames();
      this.updateTeamMemberEmails();
      this.refreshUserFiltering();
    });
  }

  // Method to refresh user filtering when team members change
  private refreshUserFiltering(): void {
    console.log('Refreshing user filtering...');
    console.log('Current team member IDs:', this.teamMembers().map(m => m.id));
    console.log('Current team member emails:', this.teamMembers().map(m => m.email));
    this.emailCtrl.updateValueAndValidity();
  }

  // Method to update team member emails when allUsers data becomes available
  private updateTeamMemberEmails(): void {
    if (this.allUsers().length === 0) return;

    const updated = this.teamMembers().map(member => {
      if (member.email === 'Loading...' || member.email === '') {
        const matchedUser = this.allUsers().find(u => u.userId === member.id);
        return {
          ...member,
          email: matchedUser?.email || 'No email available'
        };
      }
      return member;
    });
    this.teamMembers.set(updated);
  }

  // Helper method to get initials from a name
  getInitials(name: string): string {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  private _filter(value: string | User): User[] {
    // Handle both string and User objects
    const filterValue = typeof value === 'string'
      ? value.toLowerCase()
      : value?.email?.toLowerCase() || '';

    console.log('Filtering users with value:', filterValue);
    console.log('Current team members:', this.teamMembers().map(m => ({ id: m.id, email: m.email, name: m.name })));

    const filteredUsers = this.allUsers().filter(user => {
      // Skip users that are already in the chips
      if (this.emails().includes(user.email || '')) {
        console.log(`Filtering out user ${user.email} - already in chips`);
        return false;
      }

      // Skip users that already have access to the project
      // Check both by userId and by email for more robust filtering
      const hasAccess = this.teamMembers().some(member => {
        const userIdMatch = member.id === user.userId;
        const emailMatch = member.email && user.email &&
          member.email.toLowerCase() === user.email.toLowerCase();

        if (userIdMatch || emailMatch) {
          console.log(`User ${user.email} already has access - userId match: ${userIdMatch}, email match: ${emailMatch}`);
          return true;
        }
        return false;
      });

      if (hasAccess) {
        return false;
      }

      // Filter by email or name
      const matchesFilter = (user.email && user.email.toLowerCase().includes(filterValue)) ||
        (user.name && user.name.toLowerCase().includes(filterValue));

      if (matchesFilter) {
        console.log(`User ${user.email} matches filter criteria`);
      }

      return matchesFilter;
    });

    console.log(`Filtered ${filteredUsers.length} users from ${this.allUsers().length} total users`);
    return filteredUsers;
  }

  onRoleChange(roleId: number): void {
    if (this.isOperatorRoleId(roleId) && !this.hasPublishedVersions()) {
      this.toastr.warning(this.noPublishedVersionsMessage);
      this.setDefaultRoleWhenOperatorUnavailable();
      return;
    }
    this.selectedRoleId.set(roleId);
  }

  loadRoles(): void {
    const roles$ = this.roleService.getGlobalRoles?.();
    if (!roles$) {
      console.warn('Role service unavailable: using fallback roles');
      this.roles.set(this.getFallbackRoles());
      return;
    }

    roles$.pipe(
      catchError(error => {
        console.error('Error loading roles:', error);
        this.toastr.warning('Could not load role information');
        // Fallback to default roles if API fails
        return of(this.getFallbackRoles());
      })
    ).subscribe(roles => {
      const resolvedRoles = Array.isArray(roles) && roles.length > 0
        ? roles
        : this.getFallbackRoles();
      this.roles.set(resolvedRoles);
      console.log('Roles loaded:', resolvedRoles);
      this.setDefaultRoleWhenOperatorUnavailable();

      // Refresh team members display if they're already loaded
      // This ensures role names are updated
      if (this.teamMembers().length > 0) {
        this.updateRoleNames();
      }
    });
  }

  // Update role names for team members based on loaded roles
  updateRoleNames(): void {
    this.teamMembers().forEach(member => {
      const roleName = this.getRoleName(member.roleId, member.isOwner);
      console.log(`Member ${member.name} has roleId ${member.roleId}, isOwner: ${member.isOwner}, mapped to: ${roleName}`);
    });
  }

  private getFallbackRoles(): GlobalRole[] {
    return [
      { roleId: 12, name: 'admin', level: 90, permissions: [] },
      { roleId: 11, name: 'contributor', level: 50, permissions: [] },
      { roleId: 10, name: 'operator', level: 10, permissions: [] }
    ];
  }

  getRoleName(roleId: number, isOwner: boolean = false): string {
    // If the member is the project owner, always return "Owner"
    if (isOwner) {
      return 'Owner';
    }

    const role = this.roles().find(r => r.roleId === roleId);
    if (role?.name) {
      // Capitalize first letter for display
      return role.name.charAt(0).toUpperCase() + role.name.slice(1).toLowerCase();
    }

    // Fallback mapping for common role IDs if not found in the roles array
    const fallbackRoles: Record<number, string> = {
      12: 'Admin',
      11: 'Contributor',
      10: 'Operator'
    };

    return fallbackRoles[roleId] || `Role ${roleId}`;
  }

  updateMemberRole(member: TeamMember, newRoleId: number): void {
    // Skip if member is owner (can't change owner role)
    if (member.isOwner) {
      this.toastr.warning('Cannot change the project owner\'s role');
      return;
    }

    // Editing roles requires Permission::Admin, which owners and admins hold.
    if (!this.canManageTeam()) {
      this.toastr.error('Only project owners and admins can change user roles');
      return;
    }

    if (this.isOperatorRoleId(newRoleId) && !this.hasPublishedVersions()) {
      this.toastr.error(this.noPublishedVersionsMessage);
      return;
    }

    this.isLoading.set(true);

    // Call API to update member role
    const changeRequest$ = this.projectService.changeUserRole?.(this.data.projectId, member.id, newRoleId);
    if (!changeRequest$) {
      this.isLoading.set(false);
      this.toastr.error('Unable to change user role right now');
      return;
    }

    changeRequest$.pipe(
      finalize(() => this.isLoading.set(false))
    ).subscribe({
      next: () => {
        // Update local state immutably for OnPush change detection
        this.teamMembers.set(
          this.teamMembers().map(m =>
            m.id === member.id ? { ...m, roleId: newRoleId } : m
          )
        );

        this.toastr.success(`${member.name}'s role has been updated to ${this.getRoleName(newRoleId)}`);
      },
      error: (error: any) => {
        console.error('Error updating member role:', error);
        
        // Provide more specific error messages
        if (error.status === 403) {
          this.toastr.error('You do not have permission to change user roles');
        } else if (error.status === 400 && error.error?.message?.includes('owner')) {
          this.toastr.error('Cannot change the project owner\'s role');
        } else {
          this.toastr.error('Failed to update member role');
        }
        
        // Refresh the entire list in case of errors
        this.loadTeamMembers();
      }
    });
  }

  removeMember(member: TeamMember): void {
    if (member.isOwner) {
      this.toastr.warning('Cannot remove the project owner from the team');
      return;
    }

    // Removing members requires Permission::Admin, which owners and admins hold.
    if (!this.canManageTeam()) {
      this.toastr.error('Only project owners and admins can remove team members');
      return;
    }

    const isPending = !!member.isPending;
    const dialogData: RemoveTeamMemberDialogData = isPending
      ? {
          userName: member.name,
          title: 'Cancel Invite?',
          message: 'Are you sure you would like to cancel this pending invite? You can always invite them again later.',
          confirmText: 'Cancel invite',
          cancelText: 'Keep invite',
        }
      : { userName: member.name };

    this.featureDialogService.removeTeamMemberDialog(dialogData).subscribe(confirmed => {
      if (!confirmed) return;

      this.isLoading.set(true);

      const action$: Observable<any> | undefined = isPending && member.inviteId
        ? this.projectService.cancelInviteForProject?.(this.data.projectId, member.inviteId)
        : this.projectService.removeUserFromProject?.(this.data.projectId, member.id);

      if (!action$) {
        this.isLoading.set(false);
        this.toastr.error(isPending ? 'Unable to cancel invite right now' : 'Unable to remove team member right now');
        return;
      }

      action$.pipe(
        finalize(() => this.isLoading.set(false))
      ).subscribe({
        next: () => {
          this.teamMembers.set(this.teamMembers().filter(m => m.id !== member.id));
          this.toastr.success(
            isPending
              ? `Invite for ${member.name} has been cancelled`
              : `${member.name} has been removed from the team`
          );
          this.refreshUserFiltering();
        },
        error: (error: any) => {
          console.error(isPending ? 'Error cancelling invite:' : 'Error removing member:', error);
          if (error?.status === 403) {
            this.toastr.error(isPending
              ? 'You do not have permission to cancel this invite'
              : 'You do not have permission to remove team members');
          } else {
            this.toastr.error(isPending ? 'Failed to cancel invite' : 'Failed to remove team member');
          }
          this.loadTeamMembers();
        }
      });
    });
  }

  // Helper method to check if current user is the project owner
  isCurrentUserOwner(): boolean {
    return this.currentUserId() !== null &&
           this.projectOwnerId() !== null &&
           this.currentUserId() === this.projectOwnerId();
  }

  // Team operations (edit role, edit scopes, remove member) are granted
  // server-side on Permission::Admin. The owner is inserted into project_user
  // with the admin role (role_id = 12) and user_has_perm() has no separate
  // owner branch, so at the permission level Owner == Admin - every Admin must
  // get these controls, not just the owner. The Owner as a *target* is refused
  // separately via member.isOwner.
  canManageTeam(): boolean {
    if (this.isCurrentUserOwner()) {
      return true;
    }
    const uid = this.currentUserId();
    if (uid === null) {
      return false;
    }
    const self = this.teamMembers().find(m => m.id === uid && !m.isPending);
    return self?.roleId === RoleId.Admin;
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    try {
      // Get the selected option value (the user object)
      const selectedValue = event.option.value;
      console.log('Raw selected value:', selectedValue);

      // Try to parse the user object
      let userEmail: string | null = null;

      if (typeof selectedValue === 'string') {
        // If it's a string, use it directly
        userEmail = selectedValue;
        console.log('String email selected:', userEmail);
      } else if (selectedValue && typeof selectedValue === 'object') {
        // If it's an object, try to extract the email
        if (selectedValue.email) {
          userEmail = selectedValue.email;
          console.log('Object email found:', userEmail);
        } else {
          console.warn('Selected user object has no email property:', selectedValue);
        }
      } else {
        console.warn('Unknown selected value type:', typeof selectedValue);
      }

      // Add the email if we found one and it's not already in the list
      if (userEmail && !this.emails().includes(userEmail)) {
        console.log('Adding email to invite list:', userEmail);
        this.emails.set([...this.emails(), userEmail]);
        this.showEmailError.set(false);
      } else if (userEmail) {
        console.warn('Email already in list:', userEmail);
      } else {
        console.warn('No valid email found in selection');
      }
    } catch (error) {
      console.error('Error processing selection:', error);
    } finally {
      // Always reset the input field
      if (this.emailInput?.nativeElement) {
        this.emailInput.nativeElement.value = '';
      }
      this.emailCtrl.setValue(null);
    }
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add email
    if (value && this.isValidEmail(value)) {
      if (!this.emails().includes(value)) {
        this.emails.set([...this.emails(), value]);
      }
      this.showEmailError.set(false);
    } else if (value) {
      this.showEmailError.set(true);
    }

    // Clear the input value
    event.chipInput!.clear();
    this.emailCtrl.setValue(null);
  }

  remove(email: string): void {
    const index = this.emails().indexOf(email);

    if (index >= 0) {
      this.emails.set(this.emails().filter((_, i) => i !== index));
      this.announcer.announce(`Removed ${email}`);
    }

    if (this.emails().length === 0) {
      this.showEmailError.set(false);
    }
  }

  isValidEmail(email: string): boolean {
    return this.emailRegex.test(email);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onInvite(): void {
    if (this.emails().length === 0) {
      this.showEmailError.set(true);
      return;
    }

    if (this.isOperatorRoleId(this.selectedRoleId()) && !this.hasPublishedVersions()) {
      this.toastr.error(this.noPublishedVersionsMessage);
      return;
    }

    this.isLoading.set(true);

    // Initialize progress tracking
    this.inviteProgress.set({
      current: 0,
      total: this.emails().length,
      isProcessing: true
    });

    // Create a copy of emails to process
    const emailsToInvite = [...this.emails()];
    const totalEmails = emailsToInvite.length;
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    console.log(`Starting to invite ${totalEmails} users:`, emailsToInvite);

    // We need to send one invite at a time based on the API contract
    const sendInvites = (emailIndex: number = 0) => {
      if (emailIndex >= emailsToInvite.length) {
        // All invites processed
        this.isLoading.set(false);
        this.inviteProgress.set({ ...this.inviteProgress(), isProcessing: false });

        // Show summary message
        if (successCount === totalEmails) {
          this.toastr.success(`Successfully invited ${successCount} team member${successCount > 1 ? 's' : ''}`);
        } else if (successCount > 0) {
          this.toastr.warning(`Invited ${successCount} of ${totalEmails} team members. ${errorCount} failed.`);
        } else {
          this.toastr.error('Failed to invite any team members');
        }

        // Reload team members to show the new invites
        this.loadTeamMembers();
        return;
      }

      const email = emailsToInvite[emailIndex];
      console.log(`Processing invite ${emailIndex + 1}/${totalEmails}: ${email}`);

      // Update progress
      this.inviteProgress.set({ ...this.inviteProgress(), current: emailIndex + 1 });

      // Find user by email
      const user = this.allUsers().find(u => u.email?.toLowerCase() === email.toLowerCase());

      const inviteData: ProjectInviteRequest = {
        roleId: this.selectedRoleId()
      };

      if (user?.userId) {
        inviteData.inviteeId = user.userId;
      } else {
        inviteData.email = email;
      }

      console.log(`Inviting ${user?.name || email} with role ID: ${this.selectedRoleId()}`);
      console.log('Invite payload:', inviteData);

      const invite$ = this.projectService.createProjectInvite?.(this.data.projectId, inviteData);
      if (!invite$ || typeof (invite$ as any).subscribe !== 'function') {
        errorCount++;
        errors.push(`Failed to invite ${email}`);
        sendInvites(emailIndex + 1);
        return;
      }

      invite$.subscribe({
        next: () => {
          successCount++;
          console.log(`Successfully invited ${user?.name || email}`);
          sendInvites(emailIndex + 1);
        },
        error: (error) => {
          errorCount++;
          const errorMessage = `Failed to invite ${email}`;
          errors.push(errorMessage);
          console.error(`Error inviting user ${email}:`, error);
          this.toastr.error(errorMessage);
          sendInvites(emailIndex + 1);
        }
      });
    };

    sendInvites();

    // Clear the input emails
    this.emails.set([]);
  }

  // Helper method to display user in autocomplete
  displayUserFn(user: User | string | null): string {
    if (!user) return '';

    if (typeof user === 'string') return user;

    // If user has a name, display name (email)
    if (user.name && user.email) {
      return `${user.name} (${user.email})`;
    }

    return user.email || '';
  }

  // Handle keyboard events for better UX
  onKeyDown(event: KeyboardEvent): void {
    // Prevent comma from creating a new chip if no email is entered
    if (event.key === ',' && (!this.emailCtrl.value || this.emailCtrl.value === '')) {
      event.preventDefault();
      return;
    }
  }

  isOperatorRoleOption(role: Pick<GlobalRole, 'roleId' | 'name'>): boolean {
    return isOperatorRole(role.name) || role.roleId === 10 || role.roleId === 2;
  }

  isOperatorRoleId(roleId: number): boolean {
    const role = this.roles().find(item => item.roleId === roleId);
    if (!role) {
      return roleId === 10 || roleId === 2;
    }
    return this.isOperatorRoleOption(role);
  }

  private setDefaultRoleWhenOperatorUnavailable(): void {
    if (this.hasPublishedVersions() || !this.isOperatorRoleId(this.selectedRoleId())) {
      return;
    }

    const fallbackRole = this.roles().find(role => !this.isOperatorRoleOption(role));
    if (fallbackRole?.roleId) {
      this.selectedRoleId.set(fallbackRole.roleId);
    }
  }

  // --- Figma redesign helpers ---

  // Sort state
  sortAscending = signal(true);

  toggleSort(): void {
    this.sortAscending.set(!this.sortAscending());
    const sorted = [...this.teamMembers()].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);
      return this.sortAscending() ? cmp : -cmp;
    });
    this.teamMembers.set(sorted);
  }

  // Check if email is already a team member (for red chip in Figma)
  isExistingMemberEmail(email: string): boolean {
    return this.teamMembers().some(
      m => m.email.toLowerCase() === email.toLowerCase()
    );
  }

  // Selected role display helpers
  selectedRoleName(): string {
    return this.getRoleName(this.selectedRoleId());
  }

  selectedRoleClass(): string {
    return this.getRoleClassById(this.selectedRoleId());
  }

  selectedRoleIcon(): string {
    return this.getRoleIconById(this.selectedRoleId());
  }

  // Role class/icon helpers
  getRoleClass(role: Pick<GlobalRole, 'roleId' | 'name'>): string {
    return this.getRoleClassById(role.roleId!);
  }

  getRoleIcon(role: Pick<GlobalRole, 'roleId' | 'name'>): string {
    return this.getRoleIconById(role.roleId!);
  }

  // Figma-parity SVG role icons - delegates to RoleHelper which maps role name
  // to existing SVGs in assets/icons/{admin,contributor,operator,owner}.svg.
  getRoleIconPath(roleId: number, isOwner = false): string {
    if (isOwner) return RoleHelper.getRoleIconPath(UserRole.Owner);
    return RoleHelper.getRoleIconPath(RoleHelper.getRoleNameFromId(roleId));
  }

  // svgIcon name for <mat-icon svgIcon="..."> - uses the registered names from IconRegistryService
  // (e.g. 'role-admin', 'role-contributor'). Returns the `role-*` prefixed variant for consistency.
  getRoleSvgIcon(roleId: number): string {
    return RoleHelper.getRoleIcon(RoleHelper.getRoleNameFromId(roleId));
  }

  // Figma 3779-181059 dropdown labels: "Make an Admin" / "Make an Operator" /
  // "Make a Contributor". Article picked from the role name's first phoneme.
  getMakeRoleLabel(roleId: number): string {
    const name = this.getRoleName(roleId);
    const article = /^[aeiou]/i.test(name) ? 'an' : 'a';
    return `Make ${article} ${name}`;
  }

  private getRoleClassById(roleId: number): string {
    switch (roleId) {
      case 12: return 'role-admin';
      case 11: return 'role-contributor';
      case 10: return 'role-operator';
      default: return 'role-admin';
    }
  }

  private getRoleIconById(roleId: number): string {
    switch (roleId) {
      case 12: return 'A';
      case 11: return 'C';
      case 10: return 'O';
      default: return 'A';
    }
  }

  // Avatar class helper.
  // Figma: owner always uses orange; other members hash their userId across a 4-color
  // pale palette (indigo/green/purple/cyan) so the same user always gets the same color.
  // Delegates to AvatarHelper (shared/helpers/avatar.helper.ts) with class names
  // that map to the .avatar-color-N SCSS rules.
  private static readonly AVATAR_COLOR_CLASSES = [
    'avatar-color-1',
    'avatar-color-2',
    'avatar-color-3',
    'avatar-color-4',
  ];

  getMemberAvatarClass(member: TeamMember): Record<string, boolean> {
    if (member.isOwner) {
      return { 'avatar-owner': true };
    }
    const klass = AvatarHelper.getAvatarColor(
      member.id,
      InviteTeamMemberDialogComponent.AVATAR_COLOR_CLASSES,
    );
    return { [klass]: true };
  }

  // Member role class for inline badge
  getMemberRoleClass(member: TeamMember): string {
    if (member.isOwner) return 'role-owner';
    return this.getRoleClassById(member.roleId);
  }
}
